'use client';

import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { InitializePaylinkDocument } from '@packages/gql/generated/shopping/InitializePaylinkDocument';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { type PaylinkPaymentType } from '@packages/gql/generated/shopping/graphql';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEffect, useState } from 'react';
import { type PaylinkStatus } from '../../utils/paylinkStatus';
import { PaylinkSkeleton } from './PaylinkSkeleton';
import { PaylinkContent } from './PaylinkContent.client';
import { PaylinkPaymentMethods } from './PaylinkPaymentMethods';
import { PayButton } from './PayButton';

/* GraphQL */ `
  query InitializePaylink($locale: String!, $url: String!) {
    initializePaylink(locale: $locale, url: $url) {
      content {
        type
        amount
        amountWithCurrency {
          value
          currencyCode
        }
        amountType
        title
        paymentTypes
        referenceCode
      }
      error {
        errorText
        type
      }
    }
  }
`;

export const PaylinkInit = ({ status }: { status: PaylinkStatus }) => {
  const [selectedType, setSelectedType] = useState<PaylinkPaymentType>();
  const { formatMessage } = useIntl();
  const { locale } = useI18n();
  const [{ data, error }] = useAuthQuery({
    query: InitializePaylinkDocument,
    variables: { locale, url: status?.url || '' },
  });
  const [amountState, setAmountState] = useState<number>(
    data?.initializePaylink.content?.amount || 0,
  );
  const [inlineNotification, setInlineNotification] = useState<string>();

  useEffect(() => {
    (window as any).externalErrorCallback = () => {
      setSelectedType(undefined);
      setInlineNotification(
        formatMessage({
          id: 'Paylink.prepare.error.failedexternalpayment',
          defaultMessage: 'Der Zahlvorgang konnte leider nicht abgeschlossen werden.',
        }),
      );
    };

    return () => {
      (window as any).externalErrorCallback = undefined;
    };
  }, [formatMessage]);

  useEffect(() => {
    setAmountState(data?.initializePaylink.content?.amount || 0);
  }, [data]);

  if (error) {
    return (
      <Notification severity="error">
        <FormattedMessage
          key="Paylink.error.general-text"
          id="Paylink.error.general"
          defaultMessage="Es ist ein technischer Fehler aufgetreten. Bitte versuche es später nochmal."
        />
      </Notification>
    );
  }

  if (data) {
    const {
      initializePaylink: { content },
    } = data;
    if (data.initializePaylink.error) {
      return (
        <div>
          <Notification severity="error">{data.initializePaylink.error.errorText}</Notification>
        </div>
      );
    }

    return (
      <>
        {inlineNotification && <Notification severity="error">{inlineNotification}</Notification>}
        {content && (
          <PaylinkContent
            data={content}
            onAmountChange={(value) => {
              setAmountState(value);
            }}
          />
        )}
        {content?.paymentTypes && content.amount && status?.url && (
          <>
            <PaylinkPaymentMethods
              data={content.paymentTypes}
              selectedType={selectedType}
              onSelect={setSelectedType}
            />
            <div className="mt-2 flex flex-col items-center justify-around gap-2 py-2">
              <p className="text-size-body-sm text-center">
                <FormattedMessage
                  key="Paylink.prepare.submitinfo-text"
                  id="Paylink.prepare.submitinfo"
                  defaultMessage="Im Anschluss wirst du an unseren Zahlungsanbieter weitergeleitet."
                />
              </p>
              <PayButton
                amount={amountState}
                url={status.url}
                onError={setInlineNotification}
                selectedType={selectedType}
              />
            </div>
          </>
        )}
      </>
    );
  }

  return <PaylinkSkeleton />;
};
